/**
 * New range version (insert-only loader). uc-core LoadYieldBandDto + YieldBandService.load:
 *   minPct / maxPct   /^\d{1,3}(\.\d{1,2})?$/, min ≤ max
 *   minFlushKg / minInputKg  /^\d{1,8}(\.\d{1,2})?$/ (optional)
 *   effectiveFrom     YYYY-MM-DD, default today; never before the previous version's
 *   changeNote        ≤ 200, required from version 2 on; approvedById (we also ask for it from v2)
 * Rows are never edited: a change is a new version.
 */

export interface YieldBandDraft {
  cropId: string
  stepCode: string
  /** '' = every machine of the step (generic row). */
  equipmentId: string
  minPct: string
  maxPct: string
  minFlushKg: string
  minInputKg: string
  /** '' = today. */
  effectiveFrom: string
  source: 'MANUAL' | 'LEARNED'
  changeNote: string
  approvedById: string
}

export type YieldBandProblems = Partial<Record<keyof YieldBandDraft, string>>

/** The latest version of the same crop / step / machine row, if any. */
export interface PreviousVersion {
  version: number
  effectiveFrom: string | null
}

const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/
const KG_RE = /^\d{1,8}(\.\d{1,2})?$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const emptyYieldBandDraft = (p: Partial<YieldBandDraft> = {}): YieldBandDraft => ({
  cropId: '',
  stepCode: '',
  equipmentId: '',
  minPct: '',
  maxPct: '',
  minFlushKg: '',
  minInputKg: '',
  effectiveFrom: '',
  source: 'MANUAL',
  changeNote: '',
  approvedById: '',
  ...p,
})

/** Start a new version from the current row's figures. */
export function draftFromBand(b: {
  cropId: string
  stepCode: string
  equipmentId: string | null
  minBp: number | null
  maxBp: number | null
  minFlushG: number | null
  minInputG: number | null
}): YieldBandDraft {
  const pct = (bp: number | null) => (bp === null ? '' : String(bp / 100))
  const kg = (g: number | null) => (g === null ? '' : String(g / 1000))
  return emptyYieldBandDraft({
    cropId: b.cropId,
    stepCode: b.stepCode,
    equipmentId: b.equipmentId ?? '',
    minPct: pct(b.minBp),
    maxPct: pct(b.maxBp),
    minFlushKg: kg(b.minFlushG),
    minInputKg: kg(b.minInputG),
  })
}

const clean = (s: string) => s.trim().replace(/,/g, '')

/** The version this draft will become. */
export const nextVersion = (prev: PreviousVersion | null) => (prev?.version ?? 0) + 1

export function validateYieldBandDraft(
  d: YieldBandDraft,
  prev: PreviousVersion | null,
  knownSteps: readonly string[],
): YieldBandProblems {
  const p: YieldBandProblems = {}
  if (!d.cropId) p.cropId = 'Choose the crop'
  if (!d.stepCode) p.stepCode = 'Choose the step'
  else if (!knownSteps.includes(d.stepCode)) p.stepCode = 'Not a known step'

  const min = clean(d.minPct)
  const max = clean(d.maxPct)
  const pctProblem = (v: string, what: string) => {
    if (!v) return `Enter the ${what} yield`
    if (!PCT_RE.test(v)) return 'A percent with up to 2 decimals, e.g. 68 or 68.5'
    if (Number(v) > 100) return 'At most 100%'
    return undefined
  }
  p.minPct = pctProblem(min, 'lowest')
  p.maxPct = pctProblem(max, 'highest')
  if (!p.minPct && !p.maxPct && Number(min) > Number(max)) p.maxPct = 'Must not be below the lowest yield'

  for (const k of ['minFlushKg', 'minInputKg'] as const) {
    const v = clean(d[k])
    if (v && !KG_RE.test(v)) p[k] = 'Kilos with up to 2 decimals, e.g. 4 or 4.5'
  }

  const from = d.effectiveFrom.trim()
  if (from && !DATE_RE.test(from)) p.effectiveFrom = 'Pick a date'
  else if (from && prev?.effectiveFrom && from < prev.effectiveFrom)
    p.effectiveFrom = `Not before version ${prev.version} (${prev.effectiveFrom})`

  const note = d.changeNote.trim()
  if (note.length > 200) p.changeNote = 'At most 200 characters'
  if (nextVersion(prev) > 1) {
    if (!note) p.changeNote = 'Say what changed and why'
    if (!d.approvedById) p.approvedById = 'Choose who approved the new figures'
  }
  for (const k of Object.keys(p) as Array<keyof YieldBandDraft>) if (!p[k]) delete p[k]
  return p
}

export const yieldBandOk = (p: YieldBandProblems) => Object.values(p).every((v) => !v)

/** POST /yield-bands body. Call only when valid. */
export function yieldBandBody(d: YieldBandDraft) {
  const flush = clean(d.minFlushKg)
  const input = clean(d.minInputKg)
  const note = d.changeNote.trim()
  return {
    cropId: d.cropId,
    stepCode: d.stepCode,
    ...(d.equipmentId ? { equipmentId: d.equipmentId } : {}),
    minPct: clean(d.minPct),
    maxPct: clean(d.maxPct),
    ...(flush ? { minFlushKg: flush } : {}),
    ...(input ? { minInputKg: input } : {}),
    ...(d.effectiveFrom.trim() ? { effectiveFrom: d.effectiveFrom.trim() } : {}),
    source: d.source,
    ...(note ? { changeNote: note } : {}),
    ...(d.approvedById ? { approvedById: d.approvedById } : {}),
  }
}

/**
 * The latest version among the rows of the same crop / step / machine (a generic row matches
 * only generic rows); varieties are not used by the console form.
 */
export function previousVersion(
  rows: ReadonlyArray<{ cropId: string; stepCode: string; equipmentId: string | null; cropVarietyId: string | null; version: number; effectiveFrom: string | null }>,
  d: Pick<YieldBandDraft, 'cropId' | 'stepCode' | 'equipmentId'>,
): PreviousVersion | null {
  let best: PreviousVersion | null = null
  for (const r of rows) {
    if (r.cropId !== d.cropId || r.stepCode !== d.stepCode || r.cropVarietyId) continue
    if ((r.equipmentId ?? '') !== d.equipmentId) continue
    if (!best || r.version > best.version) best = { version: r.version, effectiveFrom: r.effectiveFrom }
  }
  return best
}
